import '../assets/projectCategories.css'
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Canvas, useThree, useFrame, ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Sphere } from '@react-three/drei';
import { EffectComposer, SelectiveBloom } from '@react-three/postprocessing';
// Alternative: import { Bloom } from '@react-three/postprocessing'; pour un Bloom sur toute la scène
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import * as THREE from 'three';
import gsap from 'gsap';
import WireframeShaderPlanet from './WireframedPlanet';
import MultipleRingsPlanetGroup from './RinggedPlanet';
import VeinedNeonicSphere from './NoisyPlanet';
import projectsData from '../assets/projects_data.json';

// import { Stats } from '@react-three/drei';

const PARTICLE_COUNT = 5000;
const ATTRACTOR_COUNT = 3;
const TEXTURE_SIZE = 64; // 64*64 = 4096 > 2500

const camera = new THREE.PerspectiveCamera(25, window.innerWidth / window.innerHeight, 0.1, 100);
camera.position.set(0, 7, 15);  

// GPGPU Velocity Compute Shader
const createVelocityComputeShader = () => {
  const attractorUniforms: { [key: string]: { value: THREE.Vector3 } } = {};
  const attractorAxesUniforms: { [key: string]: { value: THREE.Vector3 } } = {};
  
  for (let i = 0; i < ATTRACTOR_COUNT; i++) {
    attractorUniforms[`attractorPos${i}`] = { value: new THREE.Vector3() };
    attractorAxesUniforms[`attractorAxis${i}`] = { value: new THREE.Vector3() };
  }

  const fragmentShaderUniforms = Array.from({ length: ATTRACTOR_COUNT }, (_, i) => 
    `uniform vec3 attractorPos${i};\nuniform vec3 attractorAxis${i};`
  ).join('\n');

  const fragmentShaderLoop = Array.from({ length: ATTRACTOR_COUNT }, (_, i) => `
        {
          float dx = attractorPos${i}.x - position.x;
          float dy = attractorPos${i}.y - position.y;
          float dz = attractorPos${i}.z - position.z;
          float distSq = dx * dx + dy * dy + dz * dz;
          float dist = sqrt(distSq);

          // Gravity
          float gravityStrength = attractorMass * particleMass * gravityConstant / distSq;
          float forceMag = gravityStrength / dist;
          fx += forceMag * dx;
          fy += forceMag * dy;
          fz += forceMag * dz;

          // Spinning
          float spinningStrengthValue = gravityStrength * spinningStrength;
          float spinX = attractorAxis${i}.y * dz - attractorAxis${i}.z * dy;
          float spinY = attractorAxis${i}.z * dx - attractorAxis${i}.x * dz;
          float spinZ = attractorAxis${i}.x * dy - attractorAxis${i}.y * dx;
          fx += spinningStrengthValue * spinX;
          fy += spinningStrengthValue * spinY;
          fz += spinningStrengthValue * spinZ;
        }
  `).join('\n');

  return {
    uniforms: {
      positionTexture: { value: null },
      velocityTexture: { value: null },
      particleMassTexture: { value: null },
      timeScale: { value: 0.01 },
      maxSpeed: { value: 0.5},
      velocityDamping: { value: 0.5 },
      attractorMass: { value: 1e3 },
      gravityConstant: { value: 6.67e-11 },
      spinningStrength: { value: 1.0 },
      resolution: { value: new THREE.Vector2() },
      ...attractorUniforms,
      ...attractorAxesUniforms
    },
    fragmentShader: `
      uniform sampler2D positionTexture;
      uniform sampler2D velocityTexture;
      uniform sampler2D particleMassTexture;
      uniform float timeScale;
      uniform float maxSpeed;
      uniform float velocityDamping;
      uniform float attractorMass;
      uniform float gravityConstant;
      uniform float spinningStrength;
      uniform vec2 resolution;
      ${fragmentShaderUniforms}

      void main() {
        vec2 uv = gl_FragCoord.xy / resolution.xy;
        vec3 position = texture2D(positionTexture, uv).xyz;
        vec3 velocity = texture2D(velocityTexture, uv).xyz;
        float particleMass = texture2D(particleMassTexture, uv).x;

        // Calculate forces
        float fx = 0.0, fy = 0.0, fz = 0.0;
        
        ${fragmentShaderLoop}

        // Update velocity
        velocity.x += fx * timeScale;
        velocity.y += fy * timeScale;
        velocity.z += fz * timeScale;

        // Apply speed limit
        float speedSq = velocity.x * velocity.x + velocity.y * velocity.y + velocity.z * velocity.z;
        float maxSpeedSq = maxSpeed * maxSpeed;
        if (speedSq > maxSpeedSq) {
          float speedFactor = maxSpeed / sqrt(speedSq);
          velocity.x *= speedFactor;
          velocity.y *= speedFactor;
          velocity.z *= speedFactor;
        }

        // Apply damping
        float damping = 1.0 - velocityDamping;
        velocity.x *= damping;
        velocity.y *= damping;
        velocity.z *= damping;

        gl_FragColor = vec4(velocity, 0.0);
      }
    `
  };
};

// GPGPU Position Compute Shader
const createPositionComputeShader = () => ({
  uniforms: {
    positionTexture: { value: null },
    velocityTexture: { value: null },
    timeScale: { value: 0.01 },
    boundHalfExtent: { value: 8.0 },
    resolution: { value: new THREE.Vector2() }
  },
  fragmentShader: `
    uniform sampler2D positionTexture;
    uniform sampler2D velocityTexture;
    uniform float timeScale;
    uniform float boundHalfExtent;
    uniform vec2 resolution;

    void main() {
      vec2 uv = gl_FragCoord.xy / resolution.xy;
      vec3 position = texture2D(positionTexture, uv).xyz;
      vec3 velocity = texture2D(velocityTexture, uv).xyz;

      // Update position
      position.x += velocity.x * timeScale;
      position.y += velocity.y * timeScale;
      position.z += velocity.z * timeScale;

      // Box loop
      float twoHalfExtent = 2.0 * boundHalfExtent;
      position.x = mod(position.x + boundHalfExtent, twoHalfExtent) - boundHalfExtent;
      position.y = mod(position.y + boundHalfExtent, twoHalfExtent) - boundHalfExtent;
      position.z = mod(position.z + boundHalfExtent, twoHalfExtent) - boundHalfExtent;

      gl_FragColor = vec4(position, 1.0);
    }
  `
});

// Vertex Shader (modifié pour lire depuis les textures)
const vertexShader = `
  uniform sampler2D positionTexture;
  uniform sampler2D velocityTexture;
  uniform sampler2D particleMassTexture;
  uniform float scale;
  
  varying vec3 vVelocity;
  varying float vParticleMass;
  
  void main() {
    vec3 pos = texture2D(positionTexture, uv).xyz;
    vVelocity = texture2D(velocityTexture, uv).xyz;
    vParticleMass = texture2D(particleMassTexture, uv).x;
    
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = scale * vParticleMass * 0.25;
    gl_PointSize *= (1.85 / - mvPosition.z);
  }
`;

// Fragment Shader
const fragmentShader = `
  uniform vec3 colorA;
  uniform vec3 colorB;
  uniform float maxSpeed;
  
  varying vec3 vVelocity;
  varying float vParticleMass;
  
  void main() {
    float distanceToCenter = length(gl_PointCoord - vec2(0.5));
    if(distanceToCenter > 0.5)
            discard;

    float speed = length(vVelocity);
    float colorMix = smoothstep(0.0, 0.125, speed / maxSpeed);
    vec3 color = mix(colorA, colorB, colorMix) * 2.;
    vec3 brighter = color * 1.5;
    color = mix(color, brighter, smoothstep(0.1, 0.5, distanceToCenter));
    gl_FragColor = vec4(color, 1. - smoothstep(0.3, 0.5, distanceToCenter));
  }
`;

interface Uniforms {
  attractorMass: { value: number };
  particleGlobalMass: { value: number };
  timeScale: { value: number };
  spinningStrength: { value: number };
  maxSpeed: { value: number };
  gravityConstant: { value: number };
  velocityDamping: { value: number };
  scale: { value: number };
  boundHalfExtent: { value: number };
  colorA: { value: THREE.Color };
  colorB: { value: THREE.Color };
  attractorPositions: { value: THREE.Vector3[] };
  attractorRotationAxes: { value: THREE.Vector3[] };
} 

// Définition des attracteurs
const attractorMeshes = [
    {
        component: MultipleRingsPlanetGroup,
        props: {
            scale: 0.01
        }
    },
    {
        component: VeinedNeonicSphere,
        props: {
            scale: 1,
            radius: 0.15,
            segments: 64
        }
    },
    {
        component: WireframeShaderPlanet,
        props: {
            scale: 0.01
        }
    }
];

interface AttractorSpheresProps {
    attractorPositions: THREE.Vector3[];
    attractorRotationAxes: THREE.Vector3[];
}

interface CameraControlsProps {
    onAttractorClick: (position: THREE.Vector3) => void;
  }

  function updateAttractorsNodePositions(
    camera: THREE.Camera, 
    attractorPositions: THREE.Vector3[], 
    size: { width: number, height: number }, 
    viewport: { dpr: number }
  ) {
    const sizes = {
      width: size.width,
      height: size.height,
      pixelRatio: Math.min(viewport.dpr, 2)
    }

    for (let i = 0; i < ATTRACTOR_COUNT; i++) {
      const screenPosition = attractorPositions[i].clone()
      screenPosition.project(camera)
      const translateX = screenPosition.x * sizes.width * 0.5
      const translateY = - screenPosition.y * sizes.height * 0.5
  
      const node: HTMLElement|null = document.querySelector(attractorNodes[i])
      if(node){
        node.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`
      }
    }
  }
  
  const CameraControls: React.FC<CameraControlsProps> = () => {
    const controlsRef = useRef(null);
    const set = useThree(state => state.set);
  
    useEffect(() => {
        if (controlsRef.current) {
          set({ controls: controlsRef.current });
        }
      }, [set]);
  
    return (
      <OrbitControls 
        ref={controlsRef} 
        enableDamping 
        autoRotate 
        autoRotateSpeed={0.25}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI / 2}
      />
    );
  };

const AttractorSpheres: React.FC<AttractorSpheresProps> = ({ 
    attractorPositions, 
    attractorRotationAxes 
}) => {
    const attractorsRefs = useRef<(THREE.Group | null)[]>([]);
    const { camera, size, viewport } = useThree();
    const isAnimating = useRef(false);
    const controls = useThree(state => state.controls) as OrbitControlsImpl;

    const handleClick = (event: ThreeEvent<MouseEvent>, index: number) => {
        event.stopPropagation();
        if (isAnimating.current) return;
        
        const position = attractorPositions[index];
        handleAttractorClick(position);
    };


    const handleAttractorClick = (position: THREE.Vector3) => {
        if (isAnimating.current) return;
        isAnimating.current = true;

        // console.log("index", index)
        if (controls) {
            // console.log("disabled controls");
            controls.enabled = false;

            
            const targetPosition = position.clone().add(new THREE.Vector3(0.5, 0.5, 0.5));
            const lookAtPosition = position.clone();

            // Animer à la fois la caméra ET la target des controls
            gsap.timeline()
              .to(camera.position, {
                x: targetPosition.x,
                y: targetPosition.y,
                z: targetPosition.z + 2,
                duration: 2,
                ease: "power3.inOut",
              })
              .to(controls.target, {
                x: lookAtPosition.x,
                y: lookAtPosition.y,
                z: lookAtPosition.z,
                duration: 2,
                ease: "power3.inOut",
                onUpdate: () => {
                  camera.lookAt(controls.target);
                  updateAttractorsNodePositions(camera, attractorPositions, size, viewport);
                },
                onComplete: () => {
                  isAnimating.current = false;
                  if (controls) {
                    // console.log("enabled controls");
                    controls.enabled = true;
                  }
                }
              }, "<"); // Le "<" fait démarrer cette animation en même temps que la précédente
        }
    };

    const rotationsSpeeds = [0, 0.3, 0.8];

    useFrame((_, delta) => {
        attractorsRefs.current.forEach((attractor, i) => {
            if (attractor) {
                attractor.rotateOnWorldAxis(attractorRotationAxes[i], -delta * rotationsSpeeds[i]);
            }
        });
    });


    return (
        <>
            {attractorMeshes.map((meshData, i) => {
                const AttractorComponent = meshData.component;
                return (
                    <group
                        key={i}
                        ref={(el) => {
                            if (attractorsRefs.current.length <= i) {
                                attractorsRefs.current.push(el);
                            } else {
                                attractorsRefs.current[i] = el;
                            }
                        }}
                        position={attractorPositions[i]}
                        rotation={new THREE.Euler(0,0,0)}
                       
                    >
                        <Sphere scale={[0.15,0.15,0.15]} visible={false}  onClick={(e) => handleClick(e, i)} />
                        <AttractorComponent  quaternion={new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), attractorRotationAxes[i])} {...meshData.props} />
                    </group>
                );
            })}
        </>
    );
};

const attractorPositions =  [
    new THREE.Vector3(-0.5, 0.25, 0),
    new THREE.Vector3(0.5, 0, -0.5),
    new THREE.Vector3(0, -0.25, 0.5),
] 
const attractorRotationAxes = [
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, -0.5, 1).normalize(),
    new THREE.Vector3(0, 1, 0).normalize(),
]
const attractorNodes = [
    '.point-0',
    '.point-1',
    '.point-2'
]
interface Uniforms {
    attractorMass: THREE.IUniform<number>;
    particleGlobalMass: THREE.IUniform<number>;
    timeScale: THREE.IUniform<number>;
    spinningStrength: THREE.IUniform<number>;
    maxSpeed: THREE.IUniform<number>;
    gravityConstant: THREE.IUniform<number>;
    velocityDamping: THREE.IUniform<number>;
    scale: THREE.IUniform<number>;
    boundHalfExtent: THREE.IUniform<number>;
    colorA: THREE.IUniform<THREE.Color>;
    colorB: THREE.IUniform<THREE.Color>;
    attractorPositions: THREE.IUniform<THREE.Vector3[]>;
    attractorRotationAxes: THREE.IUniform<THREE.Vector3[]>;
    [uniform: string]: THREE.IUniform; // Signature d'index ajoutée
  }
interface ParticleSceneProps {
  particlesRef: React.RefObject<THREE.Points>;
}

const ParticleScene: React.FC<ParticleSceneProps> = ({ particlesRef }) => {
  const { gl, camera, size, viewport } = useThree();

  const [uniforms] = useState<Uniforms>({
    attractorMass: { value: 1e7 },
    particleGlobalMass: { value: 1e4 },
    timeScale: { value: 0.01 },
    spinningStrength: { value: 4 },
    maxSpeed: { value: 2 },
    gravityConstant: { value: 6.67e-11 },
    velocityDamping: { value: 0.7 },
    scale: { value: 0.008 },
    boundHalfExtent: { value: 8 },
    colorA: { value: new THREE.Color('#5900ff') },
    colorB: { value: new THREE.Color('#ffa575') },
    attractorPositions: { value: attractorPositions },
    attractorRotationAxes: { value: attractorRotationAxes },
    positionTexture: { value: null },
    velocityTexture: { value: null },
    particleMassTexture: { value: null }
  });

  // Initialize GPGPU textures and materials
  const {
    positionRenderTarget1,
    positionRenderTarget2,
    velocityRenderTarget1,
    velocityRenderTarget2,
    particleMassRenderTarget,
    positionComputeMaterial,
    velocityComputeMaterial,
    scene,
    computeCamera,
    quad,
    geometry,
    material
  } = useMemo(() => {
    // Create initial data arrays
    const positionArray = new Float32Array(TEXTURE_SIZE * TEXTURE_SIZE * 4);
    const velocityArray = new Float32Array(TEXTURE_SIZE * TEXTURE_SIZE * 4);
    const particleMassArray = new Float32Array(TEXTURE_SIZE * TEXTURE_SIZE * 4);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const i4 = i * 4;

      // Initial positions
      positionArray[i4] = (Math.random() - 0.5) * 15;
      positionArray[i4 + 1] = (Math.random() - 0.5) * 0.4;
      positionArray[i4 + 2] = (Math.random() - 0.5) * 15;
      positionArray[i4 + 3] = 1.0;

      // Initial velocities
      const phi = Math.random() * Math.PI * 2;
      const theta = Math.random() * Math.PI;
      const r = 0.05;
      velocityArray[i4] = r * Math.sin(phi) * Math.sin(theta);
      velocityArray[i4 + 1] = r * Math.cos(phi);
      velocityArray[i4 + 2] = r * Math.sin(phi) * Math.cos(theta);
      velocityArray[i4 + 3] = 0.0;

      // Particle masses
      const mass = (Math.random() * 0.75 + 0.25) * 1e4; // Using default value
      particleMassArray[i4] = mass;
      particleMassArray[i4 + 1] = 0.0;
      particleMassArray[i4 + 2] = 0.0;
      particleMassArray[i4 + 3] = 0.0;
    }

    // Fill remaining texture slots with zeros
    for (let i = PARTICLE_COUNT; i < TEXTURE_SIZE * TEXTURE_SIZE; i++) {
      const i4 = i * 4;
      positionArray[i4] = 0;
      positionArray[i4 + 1] = 0;
      positionArray[i4 + 2] = 0;
      positionArray[i4 + 3] = 1.0;
      velocityArray[i4] = 0;
      velocityArray[i4 + 1] = 0;
      velocityArray[i4 + 2] = 0;
      velocityArray[i4 + 3] = 0;
      particleMassArray[i4] = 0;
      particleMassArray[i4 + 1] = 0;
      particleMassArray[i4 + 2] = 0;
      particleMassArray[i4 + 3] = 0;
    }

    // Create data textures
    const positionTexture = new THREE.DataTexture(
      positionArray,
      TEXTURE_SIZE,
      TEXTURE_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    positionTexture.needsUpdate = true;

    const velocityTexture = new THREE.DataTexture(
      velocityArray,
      TEXTURE_SIZE,
      TEXTURE_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    velocityTexture.needsUpdate = true;

    const particleMassTexture = new THREE.DataTexture(
      particleMassArray,
      TEXTURE_SIZE,
      TEXTURE_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    particleMassTexture.needsUpdate = true;

    // Create render targets for ping-pong
    const rtOptions = {
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      generateMipmaps: false,
      stencilBuffer: false,
      depthBuffer: false
    };

    const positionRenderTarget1 = new THREE.WebGLRenderTarget(TEXTURE_SIZE, TEXTURE_SIZE, rtOptions);
    const positionRenderTarget2 = new THREE.WebGLRenderTarget(TEXTURE_SIZE, TEXTURE_SIZE, rtOptions);
    const velocityRenderTarget1 = new THREE.WebGLRenderTarget(TEXTURE_SIZE, TEXTURE_SIZE, rtOptions);
    const velocityRenderTarget2 = new THREE.WebGLRenderTarget(TEXTURE_SIZE, TEXTURE_SIZE, rtOptions);
    const particleMassRenderTarget = new THREE.WebGLRenderTarget(TEXTURE_SIZE, TEXTURE_SIZE, rtOptions);

    // Create compute materials
    const velocityShader = createVelocityComputeShader();
    const velocityComputeMaterial = new THREE.ShaderMaterial({
      uniforms: velocityShader.uniforms,
      fragmentShader: velocityShader.fragmentShader,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `
    });
    velocityComputeMaterial.uniforms.resolution.value.set(TEXTURE_SIZE, TEXTURE_SIZE);

    const positionShader = createPositionComputeShader();
    const positionComputeMaterial = new THREE.ShaderMaterial({
      uniforms: positionShader.uniforms,
      fragmentShader: positionShader.fragmentShader,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `
    });
    positionComputeMaterial.uniforms.resolution.value.set(TEXTURE_SIZE, TEXTURE_SIZE);

    // Create scene for compute passes
    const scene = new THREE.Scene();
    const computeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    scene.add(quad);

    // Initialize render targets with initial data
    const initPositionMaterial = new THREE.MeshBasicMaterial({ map: positionTexture });
    const initVelocityMaterial = new THREE.MeshBasicMaterial({ map: velocityTexture });
    const initMassMaterial = new THREE.MeshBasicMaterial({ map: particleMassTexture });

    quad.material = initPositionMaterial;
    gl.setRenderTarget(positionRenderTarget1);
    gl.render(scene, computeCamera);

    quad.material = initVelocityMaterial;
    gl.setRenderTarget(velocityRenderTarget1);
    gl.render(scene, computeCamera);

    quad.material = initMassMaterial;
    gl.setRenderTarget(particleMassRenderTarget);
    gl.render(scene, computeCamera);

    gl.setRenderTarget(null);

    // Create particle geometry with UV coordinates for texture lookup
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const uvArray = new Float32Array(PARTICLE_COUNT * 2);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const x = (i % TEXTURE_SIZE) / TEXTURE_SIZE;
      const y = Math.floor(i / TEXTURE_SIZE) / TEXTURE_SIZE;
      uvArray[i * 2] = x + 0.5 / TEXTURE_SIZE;
      uvArray[i * 2 + 1] = y + 0.5 / TEXTURE_SIZE;
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvArray, 2));

    // Create particle material
    const material = new THREE.ShaderMaterial({
      uniforms: {
        positionTexture: { value: null },
        velocityTexture: { value: null },
        particleMassTexture: { value: null },
        scale: { value: 0.008 },
        colorA: { value: new THREE.Color('#5900ff') },
        colorB: { value: new THREE.Color('#ffa575') },
        maxSpeed: { value: 2.0 }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
    //   depthTest: false,
      blending: THREE.AdditiveBlending
    });

    return {
      positionRenderTarget1,
      positionRenderTarget2,
      velocityRenderTarget1,
      velocityRenderTarget2,
      particleMassRenderTarget,
      positionComputeMaterial,
      velocityComputeMaterial,
      scene,
      computeCamera,
      quad,
      geometry,
      material
    };
  }, [gl]);

  // Ping-pong state - initialized after render targets are created
  const pingPongRef = useRef<{
    position: { read: THREE.WebGLRenderTarget; write: THREE.WebGLRenderTarget };
    velocity: { read: THREE.WebGLRenderTarget; write: THREE.WebGLRenderTarget };
  } | null>(null);

  // Initialize ping-pong after render targets are ready
  useEffect(() => {
    pingPongRef.current = {
      position: { read: positionRenderTarget1, write: positionRenderTarget2 },
      velocity: { read: velocityRenderTarget1, write: velocityRenderTarget2 }
    };
  }, [positionRenderTarget1, positionRenderTarget2, velocityRenderTarget1, velocityRenderTarget2]);
  

    useEffect( () => {
        gsap.to(camera.position, {
            duration:2,
            y: 1,
            z: screen.orientation.type.match("portrait") ? 6 : 4,
            ease:"power4.out",
            onUpdate: () => {
                // Mettre à jour les positions des nœuds pendant l'animation initiale
                updateAttractorsNodePositions(camera, attractorPositions, size, viewport);
            },
            onComplete: () => {
               document.querySelectorAll('.point').forEach((point: Element) => {
                    if(point instanceof HTMLElement){
                        point.classList.add('visible');
                    }
               })
            }
        })
        // camera.position.set(3, 4, 6);  
    }, [])


  useFrame(() => {
    updateAttractorsNodePositions(camera, attractorPositions, size, viewport);

    if (!pingPongRef.current) return;
    const pingPong = pingPongRef.current;

    // Update velocity compute shader uniforms
    velocityComputeMaterial.uniforms.timeScale.value = uniforms.timeScale.value;
    velocityComputeMaterial.uniforms.maxSpeed.value = uniforms.maxSpeed.value;
    velocityComputeMaterial.uniforms.velocityDamping.value = uniforms.velocityDamping.value;
    velocityComputeMaterial.uniforms.attractorMass.value = uniforms.attractorMass.value;
    velocityComputeMaterial.uniforms.gravityConstant.value = uniforms.gravityConstant.value;
    velocityComputeMaterial.uniforms.spinningStrength.value = uniforms.spinningStrength.value;
    velocityComputeMaterial.uniforms.positionTexture.value = pingPong.position.read.texture;
    velocityComputeMaterial.uniforms.velocityTexture.value = pingPong.velocity.read.texture;
    velocityComputeMaterial.uniforms.particleMassTexture.value = particleMassRenderTarget.texture;
    
    // Update attractor positions and axes individually
    for (let i = 0; i < ATTRACTOR_COUNT; i++) {
      velocityComputeMaterial.uniforms[`attractorPos${i}`].value.copy(uniforms.attractorPositions.value[i]);
      velocityComputeMaterial.uniforms[`attractorAxis${i}`].value.copy(uniforms.attractorRotationAxes.value[i]);
    }

    // Update velocities first
    quad.material = velocityComputeMaterial;
    gl.setRenderTarget(pingPong.velocity.write);
    gl.render(scene, computeCamera);

    // Update position compute shader uniforms
    positionComputeMaterial.uniforms.timeScale.value = uniforms.timeScale.value;
    positionComputeMaterial.uniforms.boundHalfExtent.value = uniforms.boundHalfExtent.value;
    positionComputeMaterial.uniforms.positionTexture.value = pingPong.position.read.texture;
    positionComputeMaterial.uniforms.velocityTexture.value = pingPong.velocity.write.texture;

    // Update positions with new velocities
    quad.material = positionComputeMaterial;
    gl.setRenderTarget(pingPong.position.write);
    gl.render(scene, computeCamera);

    // Swap buffers
    let temp = pingPong.position.read;
    pingPong.position.read = pingPong.position.write;
    pingPong.position.write = temp;

    temp = pingPong.velocity.read;
    pingPong.velocity.read = pingPong.velocity.write;
    pingPong.velocity.write = temp;

    // Update material uniforms for rendering
    material.uniforms.positionTexture.value = pingPong.position.read.texture;
    material.uniforms.velocityTexture.value = pingPong.velocity.read.texture;
    material.uniforms.particleMassTexture.value = particleMassRenderTarget.texture;
    material.uniforms.scale.value = uniforms.scale.value;
    material.uniforms.colorA.value = uniforms.colorA.value;
    material.uniforms.colorB.value = uniforms.colorB.value;
    material.uniforms.maxSpeed.value = uniforms.maxSpeed.value;

    gl.setRenderTarget(null);
  });

  return (
    <>
      <AttractorSpheres attractorPositions={uniforms.attractorPositions.value} attractorRotationAxes={uniforms.attractorRotationAxes.value} />
      <points ref={particlesRef} geometry={geometry} material={material} />
    </>
  );
};

interface BloomEffectProps {
  particlesRef: React.RefObject<THREE.Points>;
}

const BloomEffect: React.FC<BloomEffectProps> = ({ particlesRef }) => {
  const [particlesObject, setParticlesObject] = useState<THREE.Points | null>(null);
  
  useEffect(() => {
    if (particlesRef.current) {
      setParticlesObject(particlesRef.current);
    }
  }, [particlesRef]);
  
  if (!particlesObject) return null;
  
  return (
    <EffectComposer>
      {/* 
        OPTIMISATIONS POUR LE BLOOM:
        1. SelectiveBloom: Bloom uniquement sur les particules (pas sur les attracteurs)
        2. kernelSize={3}: Réduit le coût de calcul (3 = rapide, 5 = meilleure qualité)
        3. luminanceThreshold={0.01}: Seuil bas pour capturer les particules faibles
        4. intensity={0.4}: Intensité modérée pour équilibrer performance/qualité
        
        Alternative si SelectiveBloom ne fonctionne pas bien:
        Remplacer par <Bloom intensity={0.3} luminanceThreshold={0.01} kernelSize={3} />
      */}
      <SelectiveBloom 
        intensity={0.2}
        luminanceThreshold={0.01}
        luminanceSmoothing={0.9}
        // Bloom uniquement sur les particules
        selection={particlesObject}
        // Kernel size réduit pour performance (3 = plus rapide, 5 = meilleure qualité)
        kernelSize={3}
      />
    </EffectComposer>
  );
};

// Composant wrapper pour gérer la ref des particules
const ParticleSceneWithBloom: React.FC = () => {
  const particlesRef = useRef<THREE.Points>(null);
  
  return (
    <>
      <ParticleScene particlesRef={particlesRef} />
      <BloomEffect particlesRef={particlesRef} />
    </>
  );
};
  
  const ProjectCategories: React.FC = () => {
    const totalProjects = useMemo(() => {
      return (projectsData.conventional?.length || 0) + 
             (projectsData.creative?.length || 0) + 
             (projectsData.gamish?.length || 0);
    }, []);
    
    // const viewport = {dpr: window.devicePixelRatio}
    // const resetCamera = () => {
    //     gsap.to(camera.position, {
    //       duration: 2,
    //       x: 0,
    //       y: 2,
    //       z: 6,
    //       ease: "power4.out",
    //       onUpdate: () => {
    //         // Mettre à jour les positions des nœuds pendant le reset
    //         const canvas =  document.querySelector('canvas')
    //         const width = canvas ? canvas.width : window.innerWidth
    //         const height = canvas ? canvas.height : window.innerHeight
    //         const size = {
    //             width,
    //             height
    //         }
    //         updateAttractorsNodePositions(camera, attractorPositions, size, viewport);
    //       }
    //     });
    //   };
    return (
      <section className='project_categories'>
        {/* <Stats /> */}
        <Canvas camera={camera}>
          {/* <OrbitControls enableDamping /> */}
          <CameraControls onAttractorClick={() => {}} />
          <ParticleSceneWithBloom />
        </Canvas>
        {/* <button 
        onClick={resetCamera}
        className="reset-camera-button"
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          zIndex: 10
        }}
      >
        Reset View
      </button> */}
        <div className="point point-0">
            <span tabIndex={0} className="label"  style={{"--delay": "1s" } as React.CSSProperties}>
                <span>1</span>
            </span>
            <a href="/projects/creative" className="text">
                <span>Creative Projects</span>
            </a>
        </div>
        <div className="point point-1">
            <span tabIndex={0} className="label" style={{"--delay": "4s" } as React.CSSProperties}>
                <span>2</span>
            </span>
            <a href="/projects/gamish" className="text">
                <span>Gamish Projects</span>
            </a>
        </div>
        <div className="point point-2" style={{"--delay": "7s" } as React.CSSProperties}>
            <span tabIndex={0} className="label">
                <span>3</span>
            </span>
            <a href="/projects/conventional" className="text">
                <span>Conventional Projects</span>
            </a>
        </div>
        <div className='movable_indication'>
            <span>drag to rotate, click/tap to explore</span>
            <span>Projects to visit : <span className="projects_count">{totalProjects}</span></span>
        </div>
      </section>
    );
  };

export default ProjectCategories;
